import React, { useState, useEffect, useCallback } from 'react';
import { purchasesPaymentsMadeApi, type PaymentMadeDto } from '../../services/api';
import RecordPaymentModal from './RecordPaymentModal';

function formatPeso(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank Transfer',
  wire_transfer: 'Wire Transfer',
  check: 'Check',
  card: 'Card',
  other: 'Other',
};

export default function PaymentsMadePage() {
  const [payments, setPayments] = useState<PaymentMadeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await purchasesPaymentsMadeApi.list();
      setPayments(res.data ?? []);
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const isEmpty = !loading && payments.length === 0;

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments Made</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track payments to vendors and suppliers</p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Record Payment
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl shadow-card border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-gray-500 text-sm">Loading payments...</div>
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-sky-50 flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-sky-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Payment Tracking</h2>
            <p className="text-sm text-gray-600 max-w-md mb-6">
              Record payments made to vendors against their outstanding bills. Track check numbers,
              wire transfers, and payment schedules.
            </p>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
            >
              + Record Payment
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Vendor
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Bill
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Amount
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Method
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Reference
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDate(p.payment_date)}</td>
                    <td className="py-3 px-4 font-medium text-gray-900">{p.vendor_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{p.bill_number || '—'}</td>
                    <td className="py-3 px-4 font-medium text-gray-900">
                      {formatPeso(p.amount)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {PAYMENT_METHOD_LABELS[p.payment_method] ?? p.payment_method}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{p.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RecordPaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchPayments}
      />
    </div>
  );
}
